import { useEffect, useState, useRef } from 'react';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { styled } from 'styled-components';

const CanvasWrapper = styled.div`
  flex-grow: 1;
  width: 100%;
  min-width: 800px;
`;

/**
 * component for embedding a generative QnA experience
 *
 * @param {object} props -
 * @param {string} props.placeholder - searchbox placeholder text
 * @param {string} props.title - title to display in the embedded experience
 * @param {string} props.url - embed URL from the API
 */
export default function EmbeddedQnA({ placeholder, title, url }) {
  const [embeddingContext, setEmbeddingContext] = useState(null);
  const embedRef = useRef(null);

  /**
   * A note about the exhaustive-deps rule:
   *
   * AWS Quicksight is not a serious product, and its methods need to be called
   * in very specific, fussy ways that don't play nicely with React's hooks. If
   * we include all the actual dependencies for each effect, we end up with a
   * lot of calls to the Quicksight API that it doesn't expect, doing things
   * like destroying our embed context or invalidating the embed URL.
   *
   * To accomodate this obviously-not-ready-for-production API, we'll disable
   * the deps rule for the embed and navigation calls.
   *
   * !!! DANGER ZONE BELOW !!!
   */

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const createContext = async () => {
      const context = await createEmbeddingContext();
      setEmbeddingContext(context);
    };

    if (url) {
      createContext();
    }
  }, []);

  useEffect(() => {
    /**
     * Evan always says "let is a smell," and in this case we need to use it to
     * get around the issue of having async functions in a useEffect hook. Here, we
     * need to save a reference to the embedded object so that we can call the
     * cleanup function later. It doesn't make sense as state, because we don't
     * want to rerender when it changes.
     */
    let embeddedContent;

    const contentOptions = {
      panelOptions: {
        panelType: 'FULL',
        title,
        showQIcon: false,
      },
      showTopicName: false,
      showPinboard: false,
      allowTopicSelection: false,
      allowFullscreen: false,
      searchPlaceholderText: placeholder,
    };

    const embed = async () => {
      const frameOptions = {
        url,
        container: embedRef.current,
      };

      embeddedContent = await embeddingContext.embedGenerativeQnA(
        frameOptions,
        contentOptions,
      );
    };

    if (embeddingContext) {
      embed();
    }
    return async () => {
      if (embeddedContent) {
        await embeddedContent.close();
      }
    };
  }, [embeddingContext]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return <CanvasWrapper ref={embedRef} data-testid="canvas" />;
}
